import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ScoreBadge from "@/components/ScoreBadge";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import TimelineList from "@/components/TimelineList";
import ActivityFeed from "@/components/ActivityFeed";
import CandidateRatings from "@/components/candidate/CandidateRatings";
import CandidateNotes from "@/components/candidate/CandidateNotes";
import CandidateCompensation from "@/components/candidate/CandidateCompensation";
import CandidateInterviews from "@/components/candidate/CandidateInterviews";
import CandidateFollowups from "@/components/candidate/CandidateFollowups";
import CandidateVersions from "@/components/candidate/CandidateVersions";
import ResumeDiffPanel from "@/components/candidate/ResumeDiffPanel";
import InterviewQA from "@/components/candidate/InterviewQA";
import AnalysisNote from "@/components/candidate/AnalysisNote";
import { useAuth } from "@/context/AuthContext";
import { useAIHealth } from "@/components/AIHealthBanner";
import ScreeningProfileEditor from "@/components/candidate/ScreeningProfileEditor";
import CandidateAccordion from "@/components/candidate/CandidateAccordion";
import { SkillEvaluationGrid } from "@/pages/SkillCenter";
import AvailabilityPill from "@/components/candidate/AvailabilityPill";
import CandidateVoteBar from "@/components/CandidateVoteBar";
import ComposeEmailDialog from "@/components/ComposeEmailDialog";
import {
    Mail, Phone, MapPin, FileText, ExternalLink, ShieldAlert, Loader2,
    Trash2, X, Sparkles, CheckCircle2, AlertTriangle, Download,
    History, RefreshCw, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronRight,
    ChevronLeft, ChevronFirst, ChevronLast,
    MessageSquare, Users as UsersIcon, Star, MessageCircle, Pencil, Check,
} from "lucide-react";
import { useDocsGateStageChange } from "@/lib/useDocsGateStageChange";

/**
 * Combined candidate profile + AI analysis preview dialog.
 *
 * Tab order (per user request, Feb 2026): **AI Analysis is FIRST** because the deep
 * Claude analysis is what recruiters open the popup to see. Below the analysis card
 * lives the legacy "Generated interview questions" + skill-claim gaps + missing-info
 * blocks so the rich first-iteration analysis is no longer hidden.
 *
 * Re-analyze button hits `POST /candidates/{cid}/applications/{aid}/reanalyze` which
 * weighs recruiter notes + ratings + interview verdicts. New result persists and a
 * snapshot is appended to `analysis_versions` so score history is auditable.
 */
export default function CandidateProfileDialog({
    candidateId, open, onClose,
    applicationId = null,
    onChanged = null,
    siblings = null,       // optional: [{candidateId, applicationId}] in visible order
    siblingIndex = null,   // optional: current index into siblings
    onNavigate = null,     // optional: (newIndex) => void
}) {
    const { user } = useAuth();
    const [c, setC] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState("analysis");
    const [composeOpen, setComposeOpen] = useState(false);
    const [reload, setReload] = useState(0);
    // View mode persists across opens, default to "scroll" (single-page accordion)
    const [viewMode, setViewMode] = useState(() => {
        try { return localStorage.getItem("pw-candidate-view-mode") || "scroll"; } catch { return "scroll"; }
    });
    useEffect(() => {
        try { localStorage.setItem("pw-candidate-view-mode", viewMode); } catch { /* ignore */ }
    }, [viewMode]);

    useEffect(() => {
        if (!open || !candidateId) return;
        let cancelled = false;
        setLoading(true);
        api.get(`/candidates/${candidateId}`)
            .then(({ data }) => { if (!cancelled) setC(data); })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, candidateId, reload]);

    // Keyboard: Left/Right arrows cycle through siblings when nav is active.
    useEffect(() => {
        if (!open || !Array.isArray(siblings) || siblings.length < 2) return;
        if (typeof siblingIndex !== "number" || !onNavigate) return;
        const total = siblings.length;
        const onKey = (e) => {
            const t = e.target;
            const tag = (t?.tagName || "").toUpperCase();
            if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable) return;
            if (e.key === "ArrowRight" && siblingIndex < total - 1) {
                e.preventDefault(); onNavigate(siblingIndex + 1);
            } else if (e.key === "ArrowLeft" && siblingIndex > 0) {
                e.preventDefault(); onNavigate(siblingIndex - 1);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, siblings, siblingIndex, onNavigate]);

    const remove = async () => {
        if (!c) return;
        if (!window.confirm(`Permanently delete the infected resume for ${c.name || "this candidate"}?\nThis cannot be undone.`)) return;
        try {
            await api.post(`/candidates/${c.id}/quarantine-delete`);
            toast.success("Quarantined resume deleted");
            onClose?.();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    if (!open) return null;
    const initials = (c?.name || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
    const apps = c?.applications || [];
    const focusedApp = applicationId ? apps.find((a) => a.id === applicationId) : null;
    const qaApp = focusedApp || apps[0] || null;
    const isInfected = c?.processing_status === "infected";
    const isProcessing = c?.processing_status === "processing";
    const isAIPaused = c?.processing_status === "ai_unavailable";
    const isAIOff = c?.processing_status === "ai_off";
    const isInterviewer = user?.role === "interviewer";

    const retryParse = async () => {
        try {
            await api.post(`/candidates/${c.id}/reparse`);
            toast.success("Retrying parse — refresh the row in a few seconds");
            setReload((n) => n + 1);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent hideClose className="!p-0 !gap-0 w-screen sm:w-[96vw] max-w-none sm:max-w-5xl h-[100svh] sm:h-[92vh] max-h-[100svh] sm:max-h-[92vh] rounded-none sm:rounded-lg !inset-0 sm:!inset-auto !top-0 sm:!top-1/2 !left-0 sm:!left-1/2 !translate-x-0 sm:!translate-x-[-50%] !translate-y-0 sm:!translate-y-[-50%] [overflow:clip] !flex flex-col overscroll-contain" data-testid="cand-profile-dialog">
                <DialogTitle className="sr-only">Candidate preview — {c?.name || "Candidate"}</DialogTitle>
                <DialogDescription className="sr-only">Combined candidate profile with deep AI analysis, overview, resume, activity and compensation tabs.</DialogDescription>
                {/* Header — Phase 41 redesign: bigger avatar, name+title+chips, button strip below */}
                <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-border bg-gradient-to-b from-secondary/40 to-white shrink-0">
                    <div className="flex items-start gap-3 sm:gap-4">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-ikb text-white flex items-center justify-center font-display font-black text-base sm:text-xl shrink-0 shadow-sm">{initials}</div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                                <RenamableCandidateName candidate={c} loading={loading} onRenamed={() => { setReload((n) => n + 1); onChanged?.(); }} />
                                {isInfected && (
                                    <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-rose-300 bg-rose-50 text-rose-800 inline-flex items-center gap-1 shrink-0" data-testid="cpd-infected">
                                        <ShieldAlert className="w-3 h-3" /> Quarantined
                                    </span>
                                )}
                                {isProcessing && (
                                    <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 inline-flex items-center gap-1 shrink-0" data-testid="cpd-processing">
                                        <Loader2 className="w-3 h-3 animate-spin" /> Processing
                                    </span>
                                )}
                                {!isInterviewer && c && <AvailabilityPill candidate={c} application={focusedApp} testIdPrefix="cpd-avail" />}
                                {isAIPaused && (
                                    <button
                                        onClick={retryParse}
                                        className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 inline-flex items-center gap-1 shrink-0"
                                        title={c?.parse_error || "AI was paused — click to retry now"}
                                        data-testid="cpd-ai-retry"
                                    >
                                        <RefreshCw className="w-3 h-3" /> AI paused · Retry
                                    </button>
                                )}
                                {isAIOff && (
                                    <button
                                        onClick={retryParse}
                                        className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-ikb/40 bg-ikb/5 text-ikb hover:bg-ikb/10 inline-flex items-center gap-1 shrink-0"
                                        title="Auto-parsing is OFF for this company. Click to run AI analysis on this candidate now."
                                        data-testid="cpd-run-ai"
                                    >
                                        <Sparkles className="w-3 h-3" /> Run AI
                                    </button>
                                )}
                            </div>
                            <div className="text-xs sm:text-sm text-foreground/80 mt-1 truncate">
                                {c?.current_title || "—"}{c?.current_company ? <span className="text-muted-foreground"> · {c.current_company}</span> : null}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] font-mono text-muted-foreground">
                                {c?.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.location}</span>}
                                {typeof c?.total_experience_years === "number" && <span className="inline-flex items-center gap-1">{c.total_experience_years}y exp</span>}
                                {focusedApp?.match_score != null && <span className="inline-flex items-center gap-1 text-ikb">★ {Math.round(focusedApp.match_score)} match</span>}
                            </div>
                            {c && <RecruiterSummaryRow candidate={c} onSaved={() => { setReload((n) => n + 1); onChanged?.(); }} />}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                            {/* Prev/Next cycle through the current filtered list without closing */}
                            {Array.isArray(siblings) && siblings.length > 1 && typeof siblingIndex === "number" && (() => {
                                const total = siblings.length;
                                const atFirst = siblingIndex <= 0;
                                const atLast = siblingIndex >= total - 1;
                                const nav = "w-9 h-9 inline-flex items-center justify-center border border-border bg-white hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed";
                                return (
                                    <div className="hidden sm:flex items-center gap-1 mr-1" data-testid="cpd-nav">
                                        <button type="button" onClick={() => onNavigate?.(0)} disabled={atFirst} className={nav} title="First candidate" aria-label="First candidate" data-testid="cpd-first">
                                            <ChevronFirst className="w-4 h-4" />
                                        </button>
                                        <button type="button" onClick={() => onNavigate?.(siblingIndex - 1)} disabled={atFirst} className={nav} title="Previous candidate (←)" aria-label="Previous candidate" data-testid="cpd-prev">
                                            <ChevronLeft className="w-4 h-4" />
                                        </button>
                                        <span className="px-2 h-9 inline-flex items-center text-[11px] font-mono tabular-nums border border-border bg-secondary/40 min-w-[60px] justify-center" data-testid="cpd-counter" title="Position in the current list">
                                            {siblingIndex + 1} / {total}
                                        </span>
                                        <button type="button" onClick={() => onNavigate?.(siblingIndex + 1)} disabled={atLast} className={nav} title="Next candidate (→)" aria-label="Next candidate" data-testid="cpd-next">
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                        <button type="button" onClick={() => onNavigate?.(total - 1)} disabled={atLast} className={nav} title="Last candidate" aria-label="Last candidate" data-testid="cpd-last">
                                            <ChevronLast className="w-4 h-4" />
                                        </button>
                                    </div>
                                );
                            })()}
                            {c?.id && (
                                <Link
                                    to={`/candidates/${c.id}`}
                                    onClick={onClose}
                                    className="hidden md:inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1.5 hover:bg-secondary"
                                    data-testid="cpd-open-full"
                                >
                                    <ExternalLink className="w-3 h-3" /> Full profile
                                </Link>
                            )}
                            <button
                                onClick={async () => {
                                    setReload((n) => n + 1);
                                    toast.info(isProcessing ? "Refreshing — still processing…" : "Refreshing candidate");
                                }}
                                disabled={loading}
                                aria-label="Refresh candidate"
                                title="Refresh just this candidate's data (no full-list reload)"
                                className="w-9 h-9 inline-flex items-center justify-center border border-border bg-white hover:bg-secondary disabled:opacity-50 transition-colors"
                                data-testid="cpd-refresh"
                            >
                                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                            </button>
                            <button onClick={onClose} aria-label="Close" className="w-9 h-9 inline-flex items-center justify-center border border-border bg-white hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 transition-colors" data-testid="cpd-close">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    {/* Quick-action strip */}
                    {!isInterviewer && c && (
                        <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="cpd-quick-actions">
                            {c.phone && (
                                <a href={`tel:${c.phone}`} className="text-[10px] font-mono uppercase tracking-widest border border-ikb text-ikb bg-white px-2.5 py-1 inline-flex items-center gap-1 hover:bg-ikb hover:text-white" data-testid="cpd-quick-call"><Phone className="w-3 h-3" /> Call</a>
                            )}
                            {c.phone && (
                                <a href={`https://wa.me/${(c.phone || "").replace(/\D+/g, "")}`} target="_blank" rel="noreferrer" className="text-[10px] font-mono uppercase tracking-widest border border-ikb text-ikb bg-white px-2.5 py-1 inline-flex items-center gap-1 hover:bg-ikb hover:text-white" data-testid="cpd-quick-whatsapp"><WhatsAppIcon className="w-3 h-3" /> WhatsApp</a>
                            )}
                            {c.email && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setComposeOpen(true)}
                                        className="text-[10px] font-mono uppercase tracking-widest border border-ikb text-ikb bg-white px-2.5 py-1 inline-flex items-center gap-1 hover:bg-ikb hover:text-white"
                                        data-testid="cpd-compose-email"
                                        title="Compose a templated or custom email to this candidate"
                                    >
                                        <Mail className="w-3 h-3" /> Email
                                    </button>
                                    <a href={`mailto:${c.email}`} className="hidden sm:inline-flex text-[10px] font-mono uppercase tracking-widest border border-border text-muted-foreground bg-white px-2 py-1 items-center gap-1 hover:border-ikb hover:text-ikb" data-testid="cpd-quick-email" title="Open your mail client (mailto:)">
                                        <ExternalLink className="w-2.5 h-2.5" /> Mail app
                                    </a>
                                </>
                            )}
                            {(qaApp?.id) && (
                                <div data-testid="cpd-vote-bar-wrap">
                                    <CandidateVoteBar applicationId={qaApp.id} size="sm" />
                                </div>
                            )}
                            <div className="hidden sm:inline-flex border border-border ml-auto" data-testid="cpd-view-toggle">
                                <button
                                    type="button"
                                    onClick={() => setViewMode("scroll")}
                                    className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 ${viewMode === "scroll" ? "bg-ikb text-white" : "bg-white hover:bg-secondary"}`}
                                    data-testid="cpd-view-scroll"
                                >Scroll</button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode("tabs")}
                                    className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 ${viewMode === "tabs" ? "bg-ikb text-white" : "bg-white hover:bg-secondary"}`}
                                    data-testid="cpd-view-tabs"
                                >Tabs</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto">
                    {loading && !c ? (
                        <div className="p-10 text-center text-sm text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading candidate…
                        </div>
                    ) : !c ? null : isInfected ? (
                        <QuarantineBanner candidate={c} onDelete={remove} />
                    ) : viewMode === "scroll" ? (
                        <div className="p-3 sm:p-6 space-y-4">
                            <SkillTrendStrip c={c} />
                            {qaApp && <SkillEvaluationGrid applicationId={qaApp.id} />}
                            <CandidateAccordion
                                candidate={c}
                                focusedApp={focusedApp}
                                qaApp={qaApp}
                                onReloaded={() => setReload((n) => n + 1)}
                                currentUserId={user?.id}
                                isOwner={user?.role === "employer"}
                                storageKey={`dialog-${c.id}`}
                            />
                        </div>
                    ) : (
                        <Tabs value={tab} onValueChange={setTab} className="w-full">
                            <div className="px-2 sm:px-6 pt-2 sm:pt-3 border-b border-border bg-white sticky top-0 z-10">
                                <TabsList className="rounded-none border border-border h-9 overflow-x-auto w-full sm:w-auto flex sm:inline-flex flex-nowrap">
                                    <TabsTrigger value="analysis" className="rounded-none text-xs px-3 shrink-0 whitespace-nowrap" data-testid="cpd-tab-analysis">AI Analysis</TabsTrigger>
                                    <TabsTrigger value="overview" className="rounded-none text-xs px-3 shrink-0 whitespace-nowrap" data-testid="cpd-tab-overview">Overview</TabsTrigger>
                                    {qaApp && (
                                        <TabsTrigger value="skillgrid" className="rounded-none text-xs px-3 shrink-0 whitespace-nowrap" data-testid="cpd-tab-skillgrid">Skill Grid</TabsTrigger>
                                    )}
                                    {!isInterviewer && (
                                        <TabsTrigger value="comp" className="rounded-none text-xs px-3 shrink-0 whitespace-nowrap" data-testid="cpd-tab-comp">Compensation</TabsTrigger>
                                    )}
                                    <TabsTrigger value="followups" className="rounded-none text-xs px-3 shrink-0 whitespace-nowrap" data-testid="cpd-tab-followups">Follow-ups</TabsTrigger>
                                    <TabsTrigger value="ratings" className="rounded-none text-xs px-3 shrink-0 whitespace-nowrap" data-testid="cpd-tab-ratings">Ratings</TabsTrigger>
                                    <TabsTrigger value="interviews" className="rounded-none text-xs px-3 shrink-0 whitespace-nowrap" data-testid="cpd-tab-interviews">Interviews</TabsTrigger>
                                    <TabsTrigger value="qa" className="rounded-none text-xs px-3 shrink-0 whitespace-nowrap" data-testid="cpd-tab-qa">Interview Q&amp;A</TabsTrigger>
                                    <TabsTrigger value="notes" className="rounded-none text-xs px-3 shrink-0 whitespace-nowrap" data-testid="cpd-tab-notes">Notes</TabsTrigger>
                                    <TabsTrigger value="resume" className="rounded-none text-xs px-3 shrink-0 whitespace-nowrap" data-testid="cpd-tab-resume">Resume</TabsTrigger>
                                    <TabsTrigger value="diff" className="rounded-none text-xs px-3 shrink-0 whitespace-nowrap" data-testid="cpd-tab-diff">Resume diff</TabsTrigger>
                                    <TabsTrigger value="jobs" className="rounded-none text-xs px-3 shrink-0 whitespace-nowrap" data-testid="cpd-tab-jobs">Applications</TabsTrigger>
                                    <TabsTrigger value="activity" className="rounded-none text-xs px-3 shrink-0 whitespace-nowrap" data-testid="cpd-tab-activity">Activity</TabsTrigger>
                                </TabsList>
                            </div>
                            <div className="p-3 sm:p-6 space-y-6">
                                <TabsContent value="analysis" className="mt-0"><AnalysisPanel candidate={c} apps={apps} focusedApp={focusedApp} onReloaded={() => setReload((n) => n + 1)} /></TabsContent>
                                <TabsContent value="overview" className="mt-0"><OverviewPanel c={c} isInterviewer={isInterviewer} onPatched={() => setReload((n) => n + 1)} /></TabsContent>
                                {qaApp && (
                                    <TabsContent value="skillgrid" className="mt-0"><SkillEvaluationGrid applicationId={qaApp.id} /></TabsContent>
                                )}
                                {!isInterviewer && (
                                    <TabsContent value="comp" className="mt-0"><CandidateCompensation candidateId={c.id} /></TabsContent>
                                )}
                                <TabsContent value="followups" className="mt-0"><CandidateFollowups candidateId={c.id} /></TabsContent>
                                <TabsContent value="ratings" className="mt-0"><CandidateRatings candidateId={c.id} currentUserId={user?.id} candidateSkills={c.skills || []} /></TabsContent>
                                <TabsContent value="interviews" className="mt-0"><CandidateInterviews candidateId={c.id} /></TabsContent>
                                <TabsContent value="qa" className="mt-0"><QAPanel apps={apps} qaApp={qaApp} /></TabsContent>
                                <TabsContent value="notes" className="mt-0"><CandidateNotes candidateId={c.id} currentUserId={user?.id} isOwner={user?.role === "employer"} /></TabsContent>
                                <TabsContent value="resume" className="mt-0">
                                    <ResumePanel c={c} />
                                    <div className="mt-6"><CandidateVersions candidateId={c.id} onUpdated={() => setReload((n) => n + 1)} /></div>
                                </TabsContent>
                                <TabsContent value="diff" className="mt-0"><ResumeDiffPanel candidateId={c.id} /></TabsContent>
                                <TabsContent value="jobs" className="mt-0"><ApplicationsPanel c={c} /></TabsContent>
                                <TabsContent value="activity" className="mt-0">
                                    <ActivityFeed targetType="candidate" targetId={c.id} />
                                </TabsContent>
                            </div>
                        </Tabs>
                    )}
                </div>
            </DialogContent>
            <ComposeEmailDialog
                open={composeOpen}
                onClose={() => setComposeOpen(false)}
                candidate={c ? { ...c, _viewer_name: user?.name } : null}
            />
        </Dialog>
    );
}

/* ─── Interview Q&A panel — wraps the canonical InterviewQA component
       and lets a recruiter pick which application's Q&A to view when
       the candidate is applied to multiple jobs. ─── */
function QAPanel({ apps, qaApp }) {
    const [activeId, setActiveId] = useState(qaApp?.id || null);
    useEffect(() => { setActiveId(qaApp?.id || null); }, [qaApp?.id]);
    if (!apps || apps.length === 0) {
        return <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Add the candidate to a job to start an interview Q&amp;A.</div>;
    }
    const active = apps.find((a) => a.id === activeId) || apps[0];
    return (
        <div className="space-y-4">
            {apps.length > 1 && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="cpd-qa-app-picker">
                    <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Application:</span>
                    {apps.map((a) => (
                        <button
                            key={a.id}
                            type="button"
                            onClick={() => setActiveId(a.id)}
                            className={`text-[11px] font-mono uppercase tracking-widest px-2 py-1 border ${active?.id === a.id ? "bg-ikb text-white border-ikb" : "bg-white border-border hover:bg-secondary"}`}
                            data-testid={`cpd-qa-app-${a.id}`}
                        >
                            {a.job_title || "Job"}
                        </button>
                    ))}
                </div>
            )}
            {active ? <InterviewQA applicationId={active.id} /> : null}
        </div>
    );
}

/* ─── Applications panel (jobs tab) — mirrors the full-page list ─── */
function ApplicationsPanel({ c }) {
    const apps = c?.applications || [];
    if (apps.length === 0) {
        return <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Not assigned to any jobs yet.</div>;
    }
    return (
        <div className="border border-border bg-white divide-y divide-border" data-testid="cpd-applications-list">
            {apps.map((a) => (
                <div key={a.id} data-testid={`cpd-app-row-${a.id}`} className="flex items-center justify-between p-4 hover:bg-secondary gap-4">
                    <Link to={`/jobs/${a.job_id}`} className="flex-1 min-w-0">
                        <div className="font-medium truncate">{a.job_title || "Job"}</div>
                        <div className="text-xs text-foreground/80 font-mono">
                            Applied {a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}
                            {a.source ? ` · ${a.source}` : ""}
                            {a.stage ? ` · ${a.stage}` : ""}
                        </div>
                    </Link>
                    <ScoreBadge
                        score={a.match_score || 0}
                        size="sm"
                        processing={a.scoring_status === "processing"}
                        status={a.scoring_status === "ai_unavailable" ? "ai_unavailable" : null}
                    />
                </div>
            ))}
        </div>
    );
}

/* ─── Quarantine banner ─── */
function QuarantineBanner({ candidate, onDelete }) {
    return (
        <div className="m-3 sm:m-6 border border-rose-300 bg-rose-50 p-4 sm:p-6 space-y-4">
            <div className="inline-flex items-center gap-2 text-rose-800 font-display font-bold text-base sm:text-lg">
                <ShieldAlert className="w-5 h-5" /> Resume quarantined
            </div>
            <p className="text-sm text-rose-900/90">
                Our virus scanner flagged this file as malicious — signature: <code className="font-mono text-xs px-1.5 py-0.5 bg-white border border-rose-200">{candidate.scan_signature || "unknown"}</code>.
                The file is locked; parsing was skipped. Delete the record permanently, or ask the candidate to re-submit a clean file.
            </p>
            <button
                onClick={onDelete}
                className="inline-flex items-center gap-2 bg-rose-700 hover:bg-rose-800 text-white text-xs font-mono uppercase tracking-widest px-3 py-2"
                data-testid="cpd-quarantine-delete"
            >
                <Trash2 className="w-3.5 h-3.5" /> Permanently delete
            </button>
        </div>
    );
}

/* ─── Skill Trend Strip — reusable trend summary (used in SCROLL mode + Overview tab) ─── */
function SkillTrendStrip({ c }) {
    const [trends, setTrends] = useState({});
    useEffect(() => {
        const names = (c.skills || []).slice(0, 40);
        if (names.length === 0) { setTrends({}); return; }
        (async () => {
            try {
                const { data } = await api.get("/skill-center/skills/lookup", { params: { names: names.join(",") } });
                const map = {};
                for (const it of (data.items || [])) {
                    if (it.trend_status && ["trending", "rising", "declining", "obsolete"].includes(it.trend_status)) {
                        map[it.query.toLowerCase()] = it;
                    }
                }
                setTrends(map);
            } catch (err) { /* silent */ }
        })();
    }, [c.id, (c.skills || []).join(",")]);

    const atRisk = (c.skills || []).filter((s) => {
        const t = trends[s.toLowerCase()];
        return t && ["declining", "obsolete"].includes(t.trend_status);
    });
    const ahead = (c.skills || []).filter((s) => {
        const t = trends[s.toLowerCase()];
        return t && ["trending", "rising"].includes(t.trend_status);
    });
    if (atRisk.length === 0 && ahead.length === 0) return null;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="cpd-skill-trend-summary">
            {ahead.length > 0 && (
                <div className="border border-emerald-400 bg-emerald-50 p-2.5" data-testid="cpd-skills-ahead">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-800 mb-1">
                        ⚡ Ahead of the curve · {ahead.length}
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {ahead.slice(0, 8).map((s) => (
                            <span key={s} className="text-[10px] font-mono border border-emerald-500 bg-white text-emerald-900 px-1.5 py-0.5">{s}</span>
                        ))}
                    </div>
                </div>
            )}
            {atRisk.length > 0 && (
                <div className="border border-rose-400 bg-rose-50 p-2.5" data-testid="cpd-skills-at-risk">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-rose-800 mb-1">
                        🕰 Skills at risk · {atRisk.length}
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {atRisk.slice(0, 8).map((s) => {
                            const t = trends[s.toLowerCase()];
                            return (
                                <span
                                    key={s}
                                    className="text-[10px] font-mono border border-rose-500 bg-white text-rose-900 px-1.5 py-0.5"
                                    title={t?.trend_replaced_by ? `${t.trend_status} — modern alternative: ${t.trend_replaced_by}` : t?.trend_reason || t?.trend_status}
                                >
                                    {s}
                                    {t?.trend_replaced_by && <span className="text-emerald-700"> → {t.trend_replaced_by}</span>}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ─── Overview ─── */
function TrustScoreSparkline({ points, direction, delta }) {
    // Build a 160×38 SVG sparkline. Pure SVG, no library — keeps the dialog
    // snappy and avoids pulling heavy charting into every tab render.
    const data = (points || []).map((p) => Number(p.score) || 0);
    if (data.length < 2) {
        return (
            <div className="text-[10px] font-mono text-foreground/60" data-testid="cpd-trust-trend-empty">
                Not enough history yet — the trend will appear after the next verification event.
            </div>
        );
    }
    const w = 160, h = 38, pad = 2;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = Math.max(1, max - min);
    const stepX = (w - pad * 2) / (data.length - 1);
    const coords = data.map((v, i) => {
        const x = pad + i * stepX;
        const y = pad + (h - pad * 2) * (1 - (v - min) / range);
        return [x, y];
    });
    const path = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    const area = `${path} L${coords[coords.length - 1][0].toFixed(1)},${h - pad} L${coords[0][0].toFixed(1)},${h - pad} Z`;
    const stroke = direction === "up" ? "#047857" : direction === "down" ? "#be123c" : "#1E3A8A";
    const fill = direction === "up" ? "rgba(16,185,129,0.12)" : direction === "down" ? "rgba(244,63,94,0.12)" : "rgba(30,58,138,0.10)";
    const [lx, ly] = coords[coords.length - 1];
    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0" data-testid="cpd-trust-trend-chart" aria-label={`Trust score trend ${direction} by ${delta} points`}>
            <path d={area} fill={fill} />
            <path d={path} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx={lx} cy={ly} r="2.4" fill={stroke} />
        </svg>
    );
}

export function TrustScorePanel({ cid }) {
    const [data, setData] = useState(null);
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all([
            api.get(`/candidates/${cid}/trust-score`),
            api.get(`/candidates/${cid}/trust-score/history`).catch(() => ({ data: null })),
        ])
            .then(([score, hist]) => {
                if (cancelled) return;
                setData(score.data);
                setHistory(hist.data);
            })
            .catch(() => { if (!cancelled) setData(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [cid]);
    if (loading) {
        return (
            <section className="border border-border bg-white p-3 sm:p-4" data-testid="cpd-trust-score-loading">
                <div className="text-[10px] uppercase tracking-widest font-mono text-ikb">Trust score</div>
                <div className="text-xs font-mono text-muted-foreground mt-1">Computing…</div>
            </section>
        );
    }
    if (!data) return null;
    const bandCls =
        data.band === "high" ? "border-emerald-500 bg-emerald-50 text-emerald-900"
      : data.band === "medium" ? "border-amber-500 bg-amber-50 text-amber-900"
      : data.band === "low" ? "border-orange-500 bg-orange-50 text-orange-900"
      : "border-rose-500 bg-rose-50 text-rose-900";
    const scoreCls =
        data.band === "high" ? "text-emerald-700"
      : data.band === "medium" ? "text-amber-700"
      : data.band === "low" ? "text-orange-700"
      : "text-rose-700";
    return (
        <section className={`border ${bandCls} p-3 sm:p-4`} data-testid="cpd-trust-score">
            <div className="flex items-start gap-4 flex-wrap">
                <div className="min-w-[120px]">
                    <div className="text-[10px] uppercase tracking-widest font-mono text-foreground/70">Trust score</div>
                    <div className={`font-display font-black text-4xl sm:text-5xl mt-1 ${scoreCls}`} data-testid="cpd-trust-score-value">
                        {data.score}
                        <span className="text-base font-normal text-foreground/60"> / 100</span>
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest mt-1" data-testid="cpd-trust-score-band">
                        Band · {data.band}
                    </div>
                    {history && history.count > 0 && (
                        <div className="mt-2 flex items-center gap-2" data-testid="cpd-trust-score-trend">
                            <TrustScoreSparkline points={history.points} direction={history.direction} delta={history.delta} />
                            {history.count > 1 && (
                                <div className="min-w-0">
                                    <div className={`inline-flex items-center gap-1 text-[11px] font-mono font-bold ${history.direction === "up" ? "text-emerald-700" : history.direction === "down" ? "text-rose-700" : "text-foreground/70"}`} data-testid="cpd-trust-score-delta">
                                        {history.direction === "up" ? <TrendingUp className="w-3 h-3" /> : history.direction === "down" ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                                        {history.delta > 0 ? "+" : ""}{history.delta} pts
                                    </div>
                                    <div className="text-[10px] font-mono text-foreground/60 mt-0.5">
                                        {history.count} snapshot{history.count === 1 ? "" : "s"} · all-time
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex-1 min-w-[240px]">
                    <div className="text-[10px] uppercase tracking-widest font-mono text-foreground/70 mb-1.5">
                        Why this score
                    </div>
                    {(data.reasons || []).length === 0 ? (
                        <div className="text-xs font-mono text-foreground/70">No red flags, overlaps or comp outliers detected.</div>
                    ) : (
                        <ul className="space-y-1" data-testid="cpd-trust-score-reasons">
                            {(open ? data.reasons : data.reasons.slice(0, 3)).map((r, i) => (
                                <li key={i} className="text-xs font-mono flex items-center justify-between gap-2 border border-border bg-white px-2 py-1">
                                    <span className="truncate">{r.label}</span>
                                    <span className={`shrink-0 ${r.delta > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                                        {r.delta > 0 ? "+" : ""}{r.delta}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {(data.reasons || []).length > 3 && (
                        <button type="button" onClick={() => setOpen((o) => !o)} className="text-[10px] font-mono uppercase tracking-widest text-ikb hover:underline mt-1.5" data-testid="cpd-trust-score-toggle">
                            {open ? "Show less" : `Show all · ${data.reasons.length}`}
                        </button>
                    )}
                </div>
            </div>
        </section>
    );
}

function OverviewPanel({ c, isInterviewer, onPatched }) {
    const [trends, setTrends] = useState({});
    useEffect(() => {
        const names = (c.skills || []).slice(0, 40);
        if (names.length === 0) { setTrends({}); return; }
        (async () => {
            try {
                const { data } = await api.get("/skill-center/skills/lookup", { params: { names: names.join(",") } });
                const map = {};
                for (const it of (data.items || [])) {
                    if (it.trend_status && ["trending", "rising", "declining", "obsolete"].includes(it.trend_status)) {
                        map[it.query.toLowerCase()] = it;
                    }
                }
                setTrends(map);
            } catch (err) { /* silent */ }
        })();
    }, [c.id, (c.skills || []).join(",")]);

    const atRisk = (c.skills || []).filter((s) => {
        const t = trends[s.toLowerCase()];
        return t && ["declining", "obsolete"].includes(t.trend_status);
    });
    const ahead = (c.skills || []).filter((s) => {
        const t = trends[s.toLowerCase()];
        return t && ["trending", "rising"].includes(t.trend_status);
    });

    return (
        <div className="space-y-5 sm:space-y-6">
            {!isInterviewer && <TrustScorePanel cid={c.id} />}
            {c.ai_summary && (
                <section>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-ikb mb-1">AI summary</div>
                    <p className="text-[13px] sm:text-sm leading-relaxed">{c.ai_summary}</p>
                </section>
            )}
            <section className="grid grid-cols-3 gap-px bg-border">
                <div className="bg-white p-3 sm:p-4">
                    <div className="text-[9px] sm:text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Experience</div>
                    <div className="font-display font-black text-xl sm:text-2xl mt-1">{c.total_experience_years || 0}<span className="text-muted-foreground text-xs sm:text-sm"> yrs</span></div>
                </div>
                <div className="bg-white p-3 sm:p-4">
                    <div className="text-[9px] sm:text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Skills</div>
                    <div className="font-display font-black text-xl sm:text-2xl mt-1">{(c.skills || []).length}</div>
                </div>
                <div className="bg-white p-3 sm:p-4">
                    <div className="text-[9px] sm:text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Applications</div>
                    <div className="font-display font-black text-xl sm:text-2xl mt-1">{(c.applications || []).length}</div>
                </div>
            </section>
            {(atRisk.length > 0 || ahead.length > 0) && (
                <section className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="cpd-skill-trend-summary">
                    {ahead.length > 0 && (
                        <div className="border border-emerald-400 bg-emerald-50 p-2.5" data-testid="cpd-skills-ahead">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-800 mb-1">
                                ⚡ Ahead of the curve · {ahead.length}
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {ahead.slice(0, 8).map((s) => (
                                    <span key={s} className="text-[10px] font-mono border border-emerald-500 bg-white text-emerald-900 px-1.5 py-0.5">{s}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {atRisk.length > 0 && (
                        <div className="border border-rose-400 bg-rose-50 p-2.5" data-testid="cpd-skills-at-risk">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-rose-800 mb-1">
                                🕰 Skills at risk · {atRisk.length}
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {atRisk.slice(0, 8).map((s) => {
                                    const t = trends[s.toLowerCase()];
                                    return (
                                        <span
                                            key={s}
                                            className="text-[10px] font-mono border border-rose-500 bg-white text-rose-900 px-1.5 py-0.5"
                                            title={t?.trend_replaced_by ? `${t.trend_status} — modern alternative: ${t.trend_replaced_by}` : t?.trend_reason || t?.trend_status}
                                        >
                                            {s}
                                            {t?.trend_replaced_by && <span className="text-emerald-700"> → {t.trend_replaced_by}</span>}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </section>
            )}
            {(c.skills || []).length > 0 && (
                <section>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-2">Skills</div>
                    <div className="flex flex-wrap gap-1.5">
                        {c.skills.map((s) => {
                            const t = trends[s.toLowerCase()];
                            let cls = "border-border bg-secondary";
                            if (t?.trend_status === "trending" || t?.trend_status === "rising")
                                cls = "border-emerald-500 bg-emerald-50 text-emerald-900";
                            else if (t?.trend_status === "declining")
                                cls = "border-amber-500 bg-amber-50 text-amber-900";
                            else if (t?.trend_status === "obsolete")
                                cls = "border-rose-500 bg-rose-50 text-rose-900";
                            return (
                                <span
                                    key={s}
                                    className={`text-[11px] sm:text-xs px-2 py-0.5 border font-mono ${cls}`}
                                    title={t ? `${t.trend_status}${t.trend_score !== null && t.trend_score !== undefined ? ` · ${t.trend_score}` : ""}${t.trend_replaced_by ? ` · use ${t.trend_replaced_by}` : ""}` : undefined}
                                    data-testid={`cpd-skill-chip-${s.replace(/\s+/g, "-").toLowerCase()}`}
                                >
                                    {s}
                                </span>
                            );
                        })}
                    </div>
                </section>
            )}
            {(c.experience || []).length > 0 && (
                <section>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-2">Experience</div>
                    <TimelineList items={c.experience} type="experience" />
                </section>
            )}
            {(c.education || []).length > 0 && (
                <section>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-2">Education</div>
                    <TimelineList items={c.education} type="education" />
                </section>
            )}
            {(c.certifications || []).length > 0 && (
                <section>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-2">Certifications</div>
                    <ul className="space-y-1.5">
                        {c.certifications.map((cert, i) => (
                            <li key={i} className="text-xs font-mono border border-border bg-white px-2.5 py-1.5">
                                <span className="font-bold">{cert.name || "—"}</span>
                                {cert.issuer && <span className="text-muted-foreground"> · {cert.issuer}</span>}
                                {cert.issued_at && <span className="text-muted-foreground"> · {cert.issued_at}</span>}
                            </li>
                        ))}
                    </ul>
                </section>
            )}
            {!isInterviewer && (
                <ScreeningProfileEditor
                    candidateId={c.id}
                    initial={c.screening_profile || {}}
                    initialMeta={c.screening_profile_meta || {}}
                    onSaved={onPatched}
                />
            )}
        </div>
    );
}

/* ─── AI Analysis (the BIG one) — exported so the full-page view can reuse it ─── */
export function AnalysisPanel({ candidate, apps, focusedApp, onReloaded }) {
    if (apps.length === 0) {
        return <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Not assigned to any jobs yet. AI scoring runs when the candidate is added to a job.</div>;
    }
    // Focused app first (when opened from a kanban card / job context); the
    // rest sorted by match_score so the strongest fit reads first.
    const sorted = focusedApp
        ? [focusedApp, ...apps.filter((a) => a.id !== focusedApp.id).sort((a, b) => (b.match_score || 0) - (a.match_score || 0))]
        : [...apps].sort((a, b) => (b.match_score || 0) - (a.match_score || 0));
    // Up to 4 cards open by default so multi-application candidates aren't
    // hiding behind collapsed rows. Heavier than single-app but recruiters
    // explicitly asked to see every analysis at once.
    const DEFAULT_OPEN_LIMIT = 4;
    return (
        <div className="space-y-3" data-testid="cpd-analysis-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="cpd-analysis-summary">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    {sorted.length === 1
                        ? "AI analysis for 1 application"
                        : `${sorted.length} AI analyses across ${sorted.length} applications`}
                </div>
                {sorted.length > 1 && (
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                        Sorted by score · top {Math.min(sorted.length, DEFAULT_OPEN_LIMIT)} expanded
                    </span>
                )}
            </div>
            {sorted.map((a, i) => (
                <AnalysisCard
                    key={a.id}
                    candidate={candidate}
                    a={a}
                    defaultOpen={i < DEFAULT_OPEN_LIMIT}
                    onReloaded={onReloaded}
                />
            ))}
        </div>
    );
}

function ChangeArrow({ delta }) {
    if (!delta) return <span className="inline-flex items-center text-muted-foreground text-[11px] font-mono"><Minus className="w-3 h-3" /> 0</span>;
    const up = delta > 0;
    return (
        <span className={`inline-flex items-center gap-0.5 text-[11px] font-mono ${up ? "text-emerald-700" : "text-rose-700"}`}>
            {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {up ? "+" : ""}{delta}
        </span>
    );
}

/** Phase 155 — Inline per-application stage selector shown in the AI Analysis
 *  card header. Loads company pipeline stages once per card and PATCHes the
 *  application via the existing docs-gate hook so missing-doc guards still fire.
 */
function InlineStageSelector({ app, candidate, onChanged }) {
    const [stages, setStages] = useState([]);
    const [val, setVal] = useState(app.stage || "Applied");
    const [busy, setBusy] = useState(false);
    const { changeStage, modal } = useDocsGateStageChange();
    useEffect(() => { setVal(app.stage || "Applied"); }, [app.stage]);
    useEffect(() => {
        let alive = true;
        api.get("/company/pipeline-stages").then(({ data }) => { if (alive) setStages(data || []); }).catch(() => {});
        return () => { alive = false; };
    }, []);
    const onChange = async (e) => {
        const next = e.target.value;
        if (next === val) return;
        setBusy(true);
        try {
            const res = await changeStage(app.id, next, {
                candidate_id: candidate.id,
                candidate_name: candidate.name || "",
            });
            if (res?.ok) {
                setVal(next);
                toast.success(`Stage set to ${next}`);
                onChanged?.();
            }
        } finally { setBusy(false); }
    };
    if (stages.length === 0) {
        return (
            <div className="text-[11px] font-mono uppercase tracking-widest text-foreground/70">
                Stage: <span className="font-bold text-foreground">{val}</span>
            </div>
        );
    }
    return (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <label htmlFor={`cpd-app-stage-${app.id}`} className="text-[11px] font-mono uppercase tracking-widest text-foreground/70">Stage</label>
            <select
                id={`cpd-app-stage-${app.id}`}
                value={val}
                onChange={onChange}
                disabled={busy}
                className="text-[11px] font-mono uppercase tracking-widest border border-ikb bg-white text-ikb px-2 py-1 focus:outline-none focus:ring-2 focus:ring-ikb/30 disabled:opacity-60"
                data-testid={`cpd-app-stage-select-${app.id}`}
            >
                {stages.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                ))}
            </select>
            {modal}
        </div>
    );
}

function AnalysisCard({ candidate, a, defaultOpen, onReloaded }) {
    const [busy, setBusy] = useState(false);
    const [versions, setVersions] = useState([]);
    const [showHist, setShowHist] = useState(false);
    const [open, setOpen] = useState(defaultOpen);
    const [notes, setNotes] = useState({});
    const aiHealth = useAIHealth();
    const analysis = a.analysis || {};
    const must = analysis.must_have_analysis || { matched: [], missing: [] };
    const nice = analysis.nice_to_have_analysis || { matched: [], missing: [] };
    const matchedSkills = analysis.matched_skills || must.matched || [];
    const missingSkills = analysis.missing_skills || must.missing || [];
    const strengths = analysis.strengths || [];
    const weaknesses = analysis.weaknesses || [];
    const reds = (analysis.red_flags || []).map((r) => typeof r === "string" ? { flag: r } : r);
    const resumeGaps = analysis.resume_gaps || [];
    const skillClaimGaps = analysis.skill_claim_gaps || [];
    const missingInfo = analysis.missing_info || [];
    const interviewQs = analysis.interview_questions || [];
    const recommendation = analysis.recommendation || "—";
    const score = a.match_score || analysis.match_score || 0;
    const reasoning = analysis.score_change_reasoning;
    const lastBy = a.last_reanalyzed_by_name;

    const loadNotes = useCallback(async () => {
        try {
            const { data } = await api.get(`/applications/${a.id}/analysis-notes`);
            setNotes(data?.notes || {});
        } catch { /* silent */ }
    }, [a.id]);
    useEffect(() => { loadNotes(); }, [loadNotes]);

    const loadVersions = async () => {
        try {
            const { data } = await api.get(`/candidates/${candidate.id}/applications/${a.id}/analysis-versions`);
            setVersions(data?.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const reanalyze = async () => {
        if (busy) return;
        setBusy(true);
        toast.loading("Re-running analysis with recruiter inputs…", { id: "reana" });
        try {
            await api.post(`/candidates/${candidate.id}/applications/${a.id}/reanalyze`);
            toast.success("Analysis updated with recruiter inputs", { id: "reana" });
            onReloaded?.();
            loadVersions();
        } catch (err) {
            toast.error(formatApiError(err), { id: "reana" });
            // Likely the AI service is paused — refresh the health flag
            if (err?.response?.status === 503) aiHealth.refresh?.();
        } finally { setBusy(false); }
    };

    return (
        <div className="border border-border bg-white" data-testid={`cpd-app-${a.id}`}>
            <div className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-b border-border bg-secondary/30">
                <div className="flex items-start justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className="min-w-0 flex-1 text-left hover:bg-secondary/40 -m-1 p-1 transition-colors"
                        data-testid={`cpd-app-toggle-${a.id}`}
                        aria-expanded={open}
                    >
                        <div className="font-display font-bold text-base sm:text-lg truncate inline-flex items-center gap-1.5" data-testid={`cpd-app-title-${a.id}`}>
                            {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                            <Link onClick={(e) => e.stopPropagation()} to={`/jobs/${a.job_id}`} className="hover:text-ikb truncate">{a.job_title || "Job"}</Link>
                        </div>
                        <div className="text-[11px] font-mono uppercase tracking-widest text-foreground/70 mt-1 truncate">
                            {recommendation !== "—" ? recommendation : "No AI recommendation yet"}
                            {lastBy ? <span className="text-foreground/50"> · recruiter-informed by {lastBy}</span> : null}
                        </div>
                    </button>
                    <ScoreBadge score={score} size="md" processing={a.scoring_status === "processing"} status={a.scoring_status === "ai_unavailable" ? "ai_unavailable" : null} />
                </div>
                {/* Phase 155 — inline stage editor per application. */}
                <div className="mt-2">
                    <InlineStageSelector app={a} onChanged={onReloaded} candidate={candidate} />
                </div>
            </div>

            {open && (
                <div className="px-3 sm:px-4 py-3 sm:py-4 space-y-4">
                    {/* Re-analyze CTA + score-change strip */}
                    <div className="flex items-start justify-between gap-3 flex-wrap border border-dashed border-border p-3 bg-secondary/30">
                        <div className="min-w-0 flex-1">
                            <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground inline-flex items-center gap-2">
                                <Sparkles className="w-3 h-3 text-ikb" /> Recruiter-informed scoring
                                {analysis.is_recruiter_informed && (
                                    <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-ikb bg-blue-50 text-ikb">v{(versions.length || 0) + 1}</span>
                                )}
                            </div>
                            <p className="text-[12px] sm:text-[13px] leading-snug mt-1 text-foreground/80">
                                {reasoning || "Resume alone isn't always enough. Re-analyze to fold in recruiter notes, ratings and interviewer verdicts."}
                            </p>
                        </div>
                        <button
                            onClick={reanalyze}
                            disabled={busy || !aiHealth.available}
                            className="inline-flex items-center gap-1.5 bg-ikb hover:bg-[#001f7a] text-white text-[11px] font-mono uppercase tracking-widest px-3 py-2 disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
                            data-testid={`cpd-reanalyze-${a.id}`}
                            title={!aiHealth.available ? (aiHealth.message || "AI service is paused — try again after credits are restored") : undefined}
                        >
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            {busy ? "Analyzing…" : (!aiHealth.available ? "AI paused" : "Re-analyze")}
                        </button>
                    </div>

                    {/* Inputs counted (when recruiter-informed) */}
                    {(analysis.recruiter_inputs_considered) && (
                        <div className="flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest">
                            <span className="inline-flex items-center gap-1 border border-border bg-white px-1.5 py-0.5"><MessageSquare className="w-3 h-3" /> {analysis.recruiter_inputs_considered.notes_count || 0} notes</span>
                            <span className="inline-flex items-center gap-1 border border-border bg-white px-1.5 py-0.5"><Star className="w-3 h-3" /> {analysis.recruiter_inputs_considered.ratings_count || 0} ratings</span>
                            <span className="inline-flex items-center gap-1 border border-border bg-white px-1.5 py-0.5"><UsersIcon className="w-3 h-3" /> {analysis.recruiter_inputs_considered.interviews_count || 0} interviews</span>
                        </div>
                    )}

                    {/* Score rationale */}
                    {analysis.score_rationale && (
                        <Block label="Why this score" testid={`cpd-rationale-${a.id}`}>
                            <p className="text-[13px] leading-relaxed">{analysis.score_rationale}</p>
                        </Block>
                    )}

                    {/* Phase 66 — Claim vs Reality table (structured claims_vs_requirements) */}
                    {analysis.claims_vs_requirements && (
                        <Block label="Claim vs Reality" testid={`cpd-cvr-${a.id}`}>
                            <ClaimVsReality block={analysis.claims_vs_requirements} />
                        </Block>
                    )}

                    {/* Recruiter-input summary */}
                    {analysis.recruiter_input_summary && (
                        <Block label="What recruiters & interviewers say" testid={`cpd-recsum-${a.id}`}>
                            <p className="text-[13px] leading-relaxed">{analysis.recruiter_input_summary}</p>
                        </Block>
                    )}

                    {/* Must-have analysis */}
                    {(must.matched?.length || must.missing?.length || must.summary) && (
                        <Block label={`Must-haves (${must.score || 0}%)`}>
                            {must.summary && <p className="text-[12px] sm:text-[13px] text-muted-foreground mb-2">{must.summary}</p>}
                            <SkillCheckList matched={must.matched} missing={must.missing} testidPrefix={`mh-${a.id}`} />
                        </Block>
                    )}

                    {/* Nice-to-have analysis */}
                    {(nice.matched?.length || nice.missing?.length) && (
                        <Block label={`Nice-to-haves (${nice.score || 0}%)`}>
                            {nice.summary && <p className="text-[12px] sm:text-[13px] text-muted-foreground mb-2">{nice.summary}</p>}
                            <SkillCheckList matched={nice.matched} missing={nice.missing} testidPrefix={`nh-${a.id}`} />
                        </Block>
                    )}

                    {/* Strengths */}
                    {strengths.length > 0 && (
                        <Block label="Strengths">
                            <ul className="space-y-2">
                                {strengths.map((s, i) => (
                                    <li key={i} className="text-[12px] sm:text-[13px]">
                                        <div className="inline-flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" /><span>{s}</span></div>
                                        <AnalysisNote applicationId={a.id} block="strengths" itemKey={String(i)} notes={notes} onChange={loadNotes} />
                                    </li>
                                ))}
                            </ul>
                            <AnalysisNote applicationId={a.id} block="strengths" itemKey="__section__" notes={notes} onChange={loadNotes} compact />
                        </Block>
                    )}

                    {/* Weaknesses */}
                    {weaknesses.length > 0 && (
                        <Block label="Weaknesses">
                            <ul className="space-y-2">
                                {weaknesses.map((s, i) => (
                                    <li key={i} className="text-[12px] sm:text-[13px]">
                                        <div className="inline-flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" /><span>{s}</span></div>
                                        <AnalysisNote applicationId={a.id} block="weaknesses" itemKey={String(i)} notes={notes} onChange={loadNotes} />
                                    </li>
                                ))}
                            </ul>
                            <AnalysisNote applicationId={a.id} block="weaknesses" itemKey="__section__" notes={notes} onChange={loadNotes} compact />
                        </Block>
                    )}

                    {/* Red flags */}
                    {reds.length > 0 && (
                        <Block label="Red flags" tone="rose">
                            <ul className="space-y-2">
                                {reds.map((r, i) => (
                                    <li key={i} className="text-[12px] sm:text-[13px] text-rose-800">
                                        <div className="inline-flex items-start gap-1.5">
                                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                            <span>{r.flag}{r.severity ? <span className="text-rose-600/80 text-[10px] font-mono uppercase ml-1.5">({r.severity})</span> : null}</span>
                                        </div>
                                        <AnalysisNote applicationId={a.id} block="red_flags" itemKey={String(i)} notes={notes} onChange={loadNotes} />
                                    </li>
                                ))}
                            </ul>
                            <AnalysisNote applicationId={a.id} block="red_flags" itemKey="__section__" notes={notes} onChange={loadNotes} compact />
                        </Block>
                    )}

                    {/* Resume gaps */}
                    {resumeGaps.length > 0 && (
                        <Block label="Resume gaps">
                            <ul className="space-y-2">
                                {resumeGaps.map((g, i) => (
                                    <li key={i} className="text-[12px] sm:text-[13px]">
                                        <div><span className="font-mono text-muted-foreground">{g.period}</span> — {g.note}</div>
                                        <AnalysisNote applicationId={a.id} block="resume_gaps" itemKey={String(i)} notes={notes} onChange={loadNotes} compact />
                                    </li>
                                ))}
                            </ul>
                            <AnalysisNote applicationId={a.id} block="resume_gaps" itemKey="__section__" notes={notes} onChange={loadNotes} compact />
                        </Block>
                    )}

                    {/* Skill-claim probes (rich, originally hidden) */}
                    {skillClaimGaps.length > 0 && (
                        <Block label="Skill-claim verification probes" testid={`cpd-claims-${a.id}`}>
                            <div className="space-y-2.5">
                                {skillClaimGaps.map((g, i) => (
                                    <div key={i} className="border-l-2 border-ikb pl-3">
                                        <div className="text-[12px] sm:text-[13px] font-semibold inline-flex items-center gap-1.5">
                                            <span className="font-mono text-xs px-1.5 py-0.5 border border-border bg-secondary">{g.skill}</span>
                                            <span className="text-muted-foreground font-normal">{g.gap}</span>
                                        </div>
                                        {(g.probe_questions || []).length > 0 && (
                                            <ul className="mt-1 space-y-0.5 ml-4 list-disc text-[12px] text-foreground/80">
                                                {g.probe_questions.map((q, j) => <li key={j}>{q}</li>)}
                                            </ul>
                                        )}
                                        <AnalysisNote applicationId={a.id} block="skill_claim_gaps" itemKey={String(i)} notes={notes} onChange={loadNotes} compact />
                                    </div>
                                ))}
                            </div>
                            <AnalysisNote applicationId={a.id} block="skill_claim_gaps" itemKey="__section__" notes={notes} onChange={loadNotes} compact />
                        </Block>
                    )}

                    {/* Missing info */}
                    {missingInfo.length > 0 && (
                        <Block label="Information missing from resume">
                            <ul className="list-disc pl-5 space-y-2">
                                {missingInfo.map((m, i) => (
                                    <li key={i} className="text-[12px] sm:text-[13px] text-muted-foreground">
                                        <div>{m}</div>
                                        <AnalysisNote applicationId={a.id} block="missing_info" itemKey={String(i)} notes={notes} onChange={loadNotes} compact />
                                    </li>
                                ))}
                            </ul>
                            <AnalysisNote applicationId={a.id} block="missing_info" itemKey="__section__" notes={notes} onChange={loadNotes} compact />
                        </Block>
                    )}

                    {/* Interview questions — the canonical Interview Q&A tab is the
                        editable/answerable version. Here we surface a compact preview
                        with a link into the tab, avoiding duplication. */}
                    {interviewQs.length > 0 && (
                        <Block label={`Suggested interview questions (${interviewQs.length})`} testid={`cpd-iq-${a.id}`}>
                            <details className="group">
                                <summary className="text-[11px] font-mono uppercase tracking-widest text-ikb cursor-pointer hover:underline list-none inline-flex items-center gap-1">
                                    <span>Reveal {interviewQs.length} question{interviewQs.length === 1 ? "" : "s"}</span>
                                    <span className="text-muted-foreground group-open:hidden">→</span>
                                    <span className="text-muted-foreground hidden group-open:inline">↓</span>
                                </summary>
                                <ol className="space-y-1.5 list-decimal pl-5 mt-2">
                                    {interviewQs.slice(0, 5).map((q, i) => <li key={i} className="text-[13px] leading-snug">{q}</li>)}
                                </ol>
                                {interviewQs.length > 5 && (
                                    <p className="text-[11px] font-mono text-muted-foreground mt-2">
                                        + {interviewQs.length - 5} more · open the <span className="text-ikb">Interview Q&amp;A</span> tab to answer them.
                                    </p>
                                )}
                            </details>
                        </Block>
                    )}

                    {/* Skills evidence */}
                    {(matchedSkills.length || missingSkills.length) > 0 && (
                        <Block label="Skills evidence" testid={`cpd-skills-${a.id}`}>
                            <SkillCheckList matched={matchedSkills} missing={missingSkills} testidPrefix={`sk-${a.id}`} />
                        </Block>
                    )}

                    {/* Version history */}
                    <div className="pt-2 border-t border-border">
                        <button
                            onClick={() => { const nv = !showHist; setShowHist(nv); if (nv && versions.length === 0) loadVersions(); }}
                            className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                            data-testid={`cpd-history-toggle-${a.id}`}
                        >
                            <History className="w-3 h-3" /> {showHist ? "Hide" : "Show"} score history{versions.length ? ` (${versions.length})` : ""}
                        </button>
                        {showHist && (
                            <div className="mt-2 space-y-2" data-testid={`cpd-history-${a.id}`}>
                                {versions.length === 0 ? (
                                    <div className="text-[11px] font-mono text-muted-foreground border border-dashed border-border p-3">
                                        No re-analysis runs yet. Click <span className="text-ikb">Re-analyze</span> to record a new version.
                                    </div>
                                ) : versions.map((v) => (
                                    <div key={v.id} className="border border-border bg-white p-2.5 sm:p-3" data-testid={`cpd-version-${v.id}`}>
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <div className="text-[11px] font-mono inline-flex items-center gap-2">
                                                <span className="px-1.5 py-0.5 border border-border bg-secondary">v{v.version}</span>
                                                <span className="text-muted-foreground">{new Date(v.created_at).toLocaleString()}</span>
                                                <span className="text-muted-foreground">· by {v.triggered_by_name || "—"}</span>
                                            </div>
                                            <div className="inline-flex items-center gap-1.5">
                                                <span className="text-[11px] font-mono">{v.prev_match_score || 0} → {v.match_score}</span>
                                                <ChangeArrow delta={v.score_delta || 0} />
                                            </div>
                                        </div>
                                        {v.score_change_reasoning && (
                                            <p className="text-[12px] mt-1.5 leading-snug">{v.score_change_reasoning}</p>
                                        )}
                                        {v.recruiter_inputs_considered && (
                                            <div className="mt-1.5 flex flex-wrap gap-1.5 text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
                                                <span>{v.recruiter_inputs_considered.notes_count || 0} notes</span>
                                                <span>· {v.recruiter_inputs_considered.ratings_count || 0} ratings</span>
                                                <span>· {v.recruiter_inputs_considered.interviews_count || 0} interviews</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Fallback when nothing came back */}
                    {!analysis.score_rationale && strengths.length === 0 && reds.length === 0 && interviewQs.length === 0 && (
                        <div className="text-[12px] text-muted-foreground border border-dashed border-border p-4 text-center">
                            No detailed analysis available yet. {a.scoring_status === "processing" ? "Scoring still running — refresh in a moment." : "Click Re-analyze to generate one."}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Block({ label, children, testid, tone }) {
    return (
        <section data-testid={testid} className="space-y-2">
            <h4 className={`text-sm font-display font-bold tracking-tight ${tone === "rose" ? "text-rose-700" : "text-foreground"}`}>{label}</h4>
            {children}
        </section>
    );
}

// Phase 66 — Claim vs Reality table
// Renders a 2-column visual diff between what the candidate CLAIMS on their
// resume vs what the JOB DESCRIPTION requires. Aligned rows are emerald;
// gaps are rose; unverified claims are amber. Instantly answers "why should
// I interview them?"
function ClaimVsReality({ block }) {
    if (!block) return null;
    const claims = block.claims || [];
    const requirements = block.requirements || [];
    const aligned = new Set((block.aligned || []).map((s) => (s || "").toLowerCase()));
    const gaps = block.gaps || [];
    const unverified = block.unverified || [];
    if (!claims.length && !requirements.length && !gaps.length && !unverified.length) return null;

    const rows = Math.max(claims.length, requirements.length);
    return (
        <div className="border border-border bg-white overflow-hidden" data-testid="cpd-claim-vs-reality">
            <div className="grid grid-cols-2 text-[10px] font-mono uppercase tracking-widest bg-secondary/50 border-b border-border">
                <div className="px-3 py-2">Candidate claims</div>
                <div className="px-3 py-2 border-l border-border">JD requires</div>
            </div>
            {Array.from({ length: rows }).map((_, i) => {
                const claim = claims[i];
                const req = requirements[i];
                const claimAligned = claim && aligned.has(claim.toLowerCase());
                const reqAligned = req && aligned.has(req.toLowerCase());
                return (
                    <div key={i} className="grid grid-cols-2 text-[13px] border-b border-border/60 last:border-b-0">
                        <div className={`px-3 py-1.5 ${claimAligned ? "bg-emerald-50 text-emerald-800" : ""}`}>{claim || <span className="text-muted-foreground/60">—</span>}</div>
                        <div className={`px-3 py-1.5 border-l border-border ${reqAligned ? "bg-emerald-50 text-emerald-800" : ""}`}>{req || <span className="text-muted-foreground/60">—</span>}</div>
                    </div>
                );
            })}
            {gaps.length > 0 && (
                <div className="border-t-2 border-rose-300">
                    <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest bg-rose-50 text-rose-800 border-b border-rose-200">Gaps · JD asks, resume doesn't evidence</div>
                    {gaps.map((g, i) => (
                        <div key={`g-${i}`} className="px-3 py-1.5 text-[13px] bg-rose-50/50 text-rose-900 border-b border-rose-100 last:border-b-0">{g}</div>
                    ))}
                </div>
            )}
            {unverified.length > 0 && (
                <div className="border-t border-amber-300">
                    <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest bg-amber-50 text-amber-800 border-b border-amber-200">Unverified · needs probing</div>
                    {unverified.map((u, i) => (
                        <div key={`u-${i}`} className="px-3 py-1.5 text-[13px] bg-amber-50/50 text-amber-900 border-b border-amber-100 last:border-b-0">{u}</div>
                    ))}
                </div>
            )}
        </div>
    );
}

function SkillCheckList({ matched = [], missing = [], testidPrefix = "" }) {
    return (
        <div className="space-y-1">
            {matched.map((s, i) => (
                <div key={`m-${i}`} className="text-[12px] sm:text-[13px] inline-flex items-start gap-1.5 mr-3" data-testid={`${testidPrefix}-met-${i}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" /><span>{s}</span>
                </div>
            ))}
            <div className="flex flex-wrap gap-1.5 mt-1">
                {missing.map((s, i) => (
                    <span key={`x-${i}`} className="text-[11px] font-mono px-2 py-0.5 border border-rose-200 bg-rose-50 text-rose-800" data-testid={`${testidPrefix}-missing-${i}`}>– {s}</span>
                ))}
            </div>
        </div>
    );
}

/* ─── Resume preview ─── */
function ResumePanel({ c }) {
    if (!c.resume_filename) return <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No resume on file.</div>;
    const url = `${API}/candidates/${c.id}/resume`;
    const isPdf = (c.resume_filename || "").toLowerCase().endsWith(".pdf");
    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs font-mono text-muted-foreground inline-flex items-center gap-1.5 min-w-0">
                    <FileText className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{c.resume_filename}</span>
                </div>
                <a href={url} target="_blank" rel="noreferrer" className="text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-1.5 hover:bg-secondary inline-flex items-center gap-1" data-testid="cpd-resume-download">
                    <Download className="w-3 h-3" /> Open / download
                </a>
            </div>
            {isPdf ? (
                <object data={url} type="application/pdf" className="w-full h-[60vh] sm:h-[70vh] border border-border bg-secondary">
                    <div className="p-6 text-center text-sm text-muted-foreground">PDF preview unavailable in this browser. <a href={url} target="_blank" rel="noreferrer" className="underline">Open directly →</a></div>
                </object>
            ) : (
                <div className="p-8 text-center text-sm text-muted-foreground border border-dashed border-border">
                    Inline preview only available for PDFs. <a href={url} target="_blank" rel="noreferrer" className="text-ikb underline">Open / download →</a>
                </div>
            )}
        </div>
    );
}

/* ─── Inline-rename control for the candidate name (header H2) ─── */
export function RenamableCandidateName({ candidate, loading, onRenamed }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(candidate?.name || "");
    const [saving, setSaving] = useState(false);
    useEffect(() => { setValue(candidate?.name || ""); }, [candidate?.name]);

    if (!editing) {
        return (
            <div className="flex items-center gap-1.5 min-w-0 max-w-full flex-wrap">
                <h2 className="font-display font-black text-lg sm:text-2xl break-words min-w-0" data-testid="cpd-name">
                    {candidate?.name || (loading ? "Loading…" : "Unknown")}
                </h2>
                {candidate?.id && (
                    <button
                        type="button"
                        onClick={() => { setValue(candidate?.name || ""); setEditing(true); }}
                        title="Rename candidate"
                        aria-label="Rename candidate"
                        className="w-6 h-6 inline-flex items-center justify-center border border-border bg-white hover:bg-secondary text-muted-foreground hover:text-ikb shrink-0"
                        data-testid="cpd-rename-trigger"
                    >
                        <Pencil className="w-3 h-3" />
                    </button>
                )}
            </div>
        );
    }

    const save = async () => {
        const trimmed = (value || "").trim();
        if (!trimmed) { toast.error("Name cannot be empty"); return; }
        if (trimmed === candidate?.name) { setEditing(false); return; }
        setSaving(true);
        try {
            await api.patch(`/candidates/${candidate.id}`, { name: trimmed });
            toast.success("Candidate renamed");
            setEditing(false);
            onRenamed?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex items-center gap-1.5 min-w-0 max-w-full flex-wrap">
            <input
                autoFocus
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); save(); }
                    if (e.key === "Escape") { setEditing(false); setValue(candidate?.name || ""); }
                }}
                disabled={saving}
                maxLength={200}
                className="font-display font-black text-lg sm:text-2xl border border-ikb bg-white px-2 py-0.5 outline-none min-w-[160px] max-w-[360px] disabled:opacity-60"
                data-testid="cpd-rename-input"
                aria-label="Candidate name"
            />
            <button
                type="button"
                onClick={save}
                disabled={saving}
                title="Save name"
                aria-label="Save name"
                className="w-7 h-7 inline-flex items-center justify-center border border-ikb bg-ikb text-white hover:bg-ikb/90 disabled:opacity-60 shrink-0"
                data-testid="cpd-rename-save"
            >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            </button>
            <button
                type="button"
                onClick={() => { setEditing(false); setValue(candidate?.name || ""); }}
                disabled={saving}
                title="Cancel rename"
                aria-label="Cancel rename"
                className="w-7 h-7 inline-flex items-center justify-center border border-border bg-white hover:bg-secondary disabled:opacity-60 shrink-0"
                data-testid="cpd-rename-cancel"
            >
                <X className="w-3.5 h-3.5" />
            </button>
        </div>
    );
}

/* ─── Compact Recruiter Summary editor row (header) ─── */
export function RecruiterSummaryRow({ candidate, onSaved }) {
    const existing = candidate?.recruiter_summary || "";
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(existing);
    const [saving, setSaving] = useState(false);
    useEffect(() => { setValue(candidate?.recruiter_summary || ""); setEditing(false); }, [candidate?.id, candidate?.recruiter_summary]);

    const save = async () => {
        const trimmed = (value || "").trim();
        if (trimmed === existing.trim()) { setEditing(false); return; }
        setSaving(true);
        try {
            await api.patch(`/candidates/${candidate.id}`, { recruiter_summary: trimmed });
            toast.success("Recruiter summary saved");
            setEditing(false);
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <div className="mt-2 border border-ikb/40 bg-ikb/5 p-2" data-testid="cpd-recruiter-summary-editor">
                <div className="text-[9px] font-mono uppercase tracking-widest text-ikb mb-1 flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" /> Recruiter summary
                </div>
                <textarea
                    autoFocus
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    rows={2}
                    maxLength={2000}
                    placeholder="Short recruiter-authored summary — surfaces in the candidate list views."
                    className="w-full border border-border bg-white text-xs px-2 py-1.5 resize-y"
                    data-testid="cpd-recruiter-summary-input"
                />
                <div className="mt-1.5 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        className="inline-flex items-center gap-1 h-7 px-2 bg-ikb text-white text-[10px] font-mono uppercase tracking-widest disabled:opacity-60"
                        data-testid="cpd-recruiter-summary-save"
                    >
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
                    </button>
                    <button
                        type="button"
                        onClick={() => { setValue(existing); setEditing(false); }}
                        disabled={saving}
                        className="h-7 px-2 border border-border text-[10px] font-mono uppercase tracking-widest bg-white hover:bg-secondary"
                        data-testid="cpd-recruiter-summary-cancel"
                    >
                        Cancel
                    </button>
                    <span className="ml-auto text-[10px] font-mono text-muted-foreground">{(value || "").length}/2000</span>
                </div>
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={() => setEditing(true)}
            className="mt-2 w-full text-left border border-dashed border-border hover:border-ikb/60 hover:bg-ikb/5 px-2 py-1.5 transition-colors group"
            data-testid="cpd-recruiter-summary-row"
            title="Click to edit the recruiter summary"
        >
            <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground group-hover:text-ikb flex items-center gap-1">
                <MessageSquare className="w-3 h-3" /> Recruiter summary
                <Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 ml-1" />
            </div>
            {existing ? (
                <div className="text-xs text-foreground/85 whitespace-pre-wrap mt-0.5" data-testid="cpd-recruiter-summary-text">{existing}</div>
            ) : (
                <div className="text-xs text-muted-foreground italic mt-0.5">Add a quick recruiter summary for your team…</div>
            )}
        </button>
    );
}

